import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useDictionary } from '../../../../utilities/hooks/UseDictionary';
import { useSubscription } from '../../../../utilities/hooks/useSubscription';
import { useUpgradeToBusinessPlan } from '../../../../utilities/hooks/useUpgradeToBusinessPlan';
import { NavigatorPaths } from '../../../../assets/dictionaries/NavigatorPaths';
import PremiumBadge from '../../common/PremiumBadge';
import Tooltip from '../../common/Tooltip';
import SimpleLoader from '../../common/SimpleLoader';
import ConfirmationModal from '../../common/ConfirmationModal';
import SortOptionsSelector from '../../common/sort_options/SortOptionsSelector';
import ViewTypeSelector from '../../common/ViewTypeSelector';
import RecommendationListItem from './RecommendationListItem';
import { getRecommendations, deleteRecommendation } from './networking';

// Provider-curated recommendations (Business-plan white-label feature).
// Subscription-gated "Add new" entry, list fetch (all sections), and list/grid
// rendering. The section is surfaced per-row instead of via top-level tabs.
const Recommendations = () => {
	const dictionary = useDictionary();
	const history = useHistory();
	const isSubscription = useSubscription();
	const goToUpgrade = useUpgradeToBusinessPlan();

	const [items, setItems] = useState([]);
	const [showLoader, setShowLoader] = useState(true);
	const [showDeleteDialog, setDeleteDialogVisibility] = useState(false);
	const [selectedDelete, setSelectedDelete] = useState(null);
	const [sortOption, setSortOption] = useState(null);
	const [viewType, setViewType] = useState(
		parseInt(localStorage.getItem('recommendations_selected_view_type') ?? 0)
	);

	const source = axios.CancelToken.source();

	useEffect(() => {
		getRecommendationsAsync();
	}, []);

	useEffect(() => {
		return () => {
			source.cancel('component unmounted');
		};
	}, []);

	const getRecommendationsAsync = async () => {
		setShowLoader(true);
		try {
			const { data } = await getRecommendations(null, source.token);
			setItems(data.data ?? []);
		} catch (error) {
			if (!axios.isCancel(error)) {
				setItems([]);
			}
		} finally {
			setShowLoader(false);
		}
	};

	const onAddClicked = () => {
		if (!isSubscription) {
			goToUpgrade();
			return;
		}
		history.push('/' + NavigatorPaths.recommendations_add.path);
	};

	const onDeleteClicked = (recommendation) => {
		setSelectedDelete(recommendation);
		setDeleteDialogVisibility(true);
	};

	const deleteRecommendationAsync = async (onComplete, onFail) => {
		try {
			await deleteRecommendation(selectedDelete.id);
			onComplete();
		} catch (error) {
			onFail();
		}
	};

	// The list endpoint returns sort_order-ordered rows; the sort dropdown
	// re-orders client-side (no server sort param on this surface).
	const sortItems = (list) => {
		if (!sortOption) return list;
		const dir = sortOption.direction === 'asc' ? 1 : -1;
		const key = sortOption.value;
		return [...list].sort((a, b) => {
			const av = a?.[key];
			const bv = b?.[key];
			if (typeof av === 'string' || typeof bv === 'string') {
				return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
			}
			return ((av ?? 0) - (bv ?? 0)) * dir;
		});
	};

	const sortedItems = sortItems(items);

	return (
		<div className="experiences-wrapper">
			<div className="experiences-top-bar">
				<div className="d-flex items-center">
					<h2 className="title-main">{dictionary.recommendations}</h2>
					<div
						className="text-with-info tooltip-trigger"
						style={{ marginLeft: '8px' }}
					>
						<Tooltip content={dictionary.recommendations_hint_tooltip} />
					</div>
					<div className="d-flex items-center" style={{ marginLeft: '24px' }}>
						<button
							className={`button has-premium-badge${
								!isSubscription ? ' button--locked' : ''
							}`}
							onClick={onAddClicked}
							data-testid="add-recommendation-button"
						>
							<div className={isSubscription ? 'plus-icon' : 'plus-dark-icon'} />
							{dictionary.add_recommendation}
							{!isSubscription ? <PremiumBadge /> : null}
						</button>
					</div>
				</div>
				<div className="flex-filler" />
				<SortOptionsSelector
					onValueChanged={setSortOption}
					name={'recommendations'}
				/>
				<div style={{ width: '16px' }} />
				<ViewTypeSelector
					onValueChanged={setViewType}
					name={'recommendations'}
				/>
			</div>

			{showLoader ? (
				<SimpleLoader />
			) : items.length === 0 ? (
				<div className="empty-experiences-list-wrapper">
					<div className="empty-experiences-list-placeholder">
						<div className="empty-experiences-image" />
						<h3 style={{ textAlign: 'center', marginTop: '24px' }}>
							{dictionary.recommendations_empty}
						</h3>
						<div style={{ marginTop: '8px' }} className="text">
							{dictionary.recommendations_empty_subtitle}
						</div>
					</div>
				</div>
			) : (
				<div
					className={
						viewType === 0
							? 'experience-items-wrapper thin-scrollbar'
							: 'experience-items-wrapper thin-scrollbar tile-experience-items-wrapper'
					}
				>
					{sortedItems.map((item) => (
						<RecommendationListItem
							key={item.id}
							recommendation={item}
							onDeleteClicked={onDeleteClicked}
							compact={viewType === 1}
						/>
					))}
				</div>
			)}

			{showDeleteDialog ? (
				<ConfirmationModal
					onActionFinished={() => getRecommendationsAsync()}
					setShowModal={setDeleteDialogVisibility}
					asyncTask={deleteRecommendationAsync}
					title={dictionary.delete_recommendation}
					message={dictionary.delete_recommendation_confirm}
					successMessage={dictionary.recommendation_deleted}
					errorMessage={dictionary.problem_deleting_recommendation}
					action={dictionary.delete}
				/>
			) : null}
		</div>
	);
};

export default Recommendations;
